import { ChangeDetectorRef, Component, forwardRef, inject, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { BarcodeScannerInstanceToken, BarcodeScannerInterface } from '@evorait/pace-core';
import { take } from 'rxjs';

type ChangeCallbackFn = (event: unknown) => void;

@Component({
    selector: 'pace-forms-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropdownComponent),
            multi: true,
        },
    ],
})
export class DropdownComponent implements ControlValueAccessor {
    @Input()
    public disabled = false;

    @Input()
    public insideClick = false;

    @Input()
    public dropDownText: string;

    /** Renders a text input instead of the button as the dropdown toggle. */
    @Input()
    public useInputField = false;

    /** Whether there are dropdown items to show; hides the caret and disables opening when false. */
    @Input()
    public hasOptions = true;

    /** Id of the text input rendered by `useInputField`, so a label can reference it. */
    @Input()
    public inputId: string;

    /** Placeholder of the text input rendered by `useInputField`. */
    @Input()
    public inputPlaceholder: string;

    @Input()
    public showScanner = false;

    public value: string;
    public faBarcode = faBarcode;
    public defaultDropDownButtonText: string;
    public onChanged: ChangeCallbackFn = () => undefined;
    public onTouched: VoidFunction = () => undefined;

    private readonly barcodeScannerService = inject<BarcodeScannerInterface>(BarcodeScannerInstanceToken);

    public constructor(
        private readonly translate: TranslateService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this.defaultDropDownButtonText = this.translate.instant('F4M_CONTENT_RENDER_SELECT_ANSWER_LABEL');
    }

    public writeValue(val: string): void {
        if (this.value === val) {
            this.cdr.markForCheck();
            return;
        }
        this.value = val;
        this.cdr.markForCheck();
    }

    public registerOnChange(fn: ChangeCallbackFn): void {
        this.onChanged = fn;
    }

    public onInput(event: Event): void {
        this.value = (event.target as HTMLInputElement).value;
        this.onChanged(this.value);
    }

    public registerOnTouched(fn: VoidFunction): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.cdr.markForCheck();
    }

    public openScanner(event: Event): void {
        event.stopPropagation();
        this.barcodeScannerService
            .scan()
            .pipe(take(1))
            .subscribe((value: { text: string; format: string }) => {
                this.value = value.text;
                this.onChanged(value.text);
            });
    }
}
